import { type Remote, wrap } from "comlink";

/** What a call to a panel's worker rejects with once the panel has terminated that worker. */
export class WorkerTerminatedError extends Error {
	constructor() {
		super("The performance worker was terminated before the call settled");
		this.name = "WorkerTerminatedError";
	}
}

/**
 * Wraps a worker with comlink, and returns it with a `terminate` that also rejects every call still in
 * flight — and every later one — with {@link WorkerTerminatedError}.
 *
 * A comlink call to a terminated worker otherwise never settles. The panel terminates its worker on
 * unmount, and it can unmount while its queries are refetching (a bot-write invalidation re-renders the
 * charts it sits in), so a call that never settles is a refetch that never settles — and
 * `invalidateQueries` awaits every one, the SphereAI post-write refresh included.
 */
export function wrapTerminableWorker<T>(worker: Worker): readonly [Remote<T>, () => void] {
	const remote = wrap<T>(worker) as unknown as Record<string, (...args: unknown[]) => Promise<unknown>>;
	let rejectTerminated: (error: Error) => void = () => {};
	const terminated = new Promise<never>((_, reject) => {
		rejectTerminated = reject;
	});
	// Observed here so a termination with nothing in flight is not an unhandled rejection.
	terminated.catch(() => {});

	const api = new Proxy({} as Remote<T>, {
		get(_target, name) {
			// Not a thenable, and exposes no symbols: only the worker's own methods are forwarded.
			if (typeof name !== "string" || name === "then") {
				return undefined;
			}
			return (...args: unknown[]) => Promise.race([remote[name](...args), terminated]);
		},
	});

	return [
		api,
		() => {
			worker.terminate();
			rejectTerminated(new WorkerTerminatedError());
		},
	] as const;
}
